<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        md="4"
        class="flex--col"
      >
        <VDateTimePicker
          v-model="data.onLocationFrom"
          dense
          :label="$t('forms.labels.onLocationFrom')"
          :rules="{ required: false }"
          :locale="locale"
          :disabled="isCompletedOrLocked"
          :dataCy="dataCy.workOrders.workOrderView.inputs.onLocationFrom"
          @input="debouncedInput('onLocationFrom')"
        />
        <VDateTimePicker
          v-model="data.onLocationTo"
          dense
          :label="$t('forms.labels.onLocationTo')"
          :rules="{ required: false }"
          :locale="locale"
          :disabled="isCompletedOrLocked"
          :dataCy="dataCy.workOrders.workOrderView.inputs.onLocationTo"
          @input="debouncedInput('onLocationTo')"
        />
        <VAutocomplete
          v-model="data.assignedTo"
          outlined
          dense
          :label="$t('forms.labels.assignedTo')"
          :items="displayedMembers"
          :disabled="isDisabled"
          :readonly="isCompletedOrLocked"
          :datacy="dataCy.workOrders.workOrderView.inputs.assignedTo"
          @input="debouncedInput('assignedTo', [data.assignedTo])"
        />
      </v-col>
      <v-col
        cols="12"
        md="4"
      >
        <div class="subtitle-2">
          {{ $t('forms.labels.completionStatus') }}
        </div>
        <v-radio-group
          v-model="data.completionStatus"
          dense
        >
          <v-radio
            v-for="item in completionStatus"
            :key="item.key"
            :label="item.name"
            :value="item.key"
            :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked || item.isDisabled"
            :dataCy="`${dataCy.workOrders.workOrderView.inputs.completionStatus}--${item.key}`"
            color="primary"
            onIcon="check_box"
            offIcon="check_box_outline_blank"
            :readonly="!isEdition"
            @change="debouncedInput('completionStatus')"
          />
        </v-radio-group>
      </v-col>
      <v-col
        cols="12"
        md="4"
        class="d-flex flex-column"
      >
        <div class="subtitle-2">
          {{ $t('forms.labels.priority') }}
        </div>
        <v-row
          align="center"
          style="width: 100%"
        >
          <v-slider
            v-model="tempPriority"
            dense
            :max="priorityStatus.length - 1"
            step="1"
            thumbLabel="always"
            :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked "
            :trackColor="getActivePriorityStatusColor"
            :thumbColor="getActivePriorityStatusColor"
            :thumbSize="50"
            :trackFillColor="getActivePriorityStatusColor"
            :readonly="!isEdition"
            :data-cy="dataCy.workOrders.workOrderView.inputs.priority"
            @change="debouncedInput('priority', priorityStatus[tempPriority].key)"
          >
            <template v-slot:thumb-label="{ value }">
              {{ priorityStatus[value].name }}
            </template>
          </v-slider>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment';
import { debounce } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';
import workOrderStatusChoices from '@/constants/workOrderStatusChoices';
import roles from '@/constants/roles';
import VDateTimePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDateTimePicker.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete';
import _datePickerLocales from '@/locales/date-picker-locales';
import { workOrders } from '@/aq-data-cy.json';

export default {
    components: {
        VDateTimePicker,
        VAutocomplete,
    },
    mixins: [
        validationProviderHelperMixin,
    ],
    props: {
        workOrder: {
            type: Object,
            default: null,
        },
        isCompletedOrLocked: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            members: [],
            assignedTo: {},
            tempPriority: 0,
            isEdition: true,
            debouncedInput: debounce(this.onInput, 500),
            data: {
                onLocationFrom: null,
                onLocationTo: null,
                assignedTo: '',
                completionStatus: '',
            },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'user',
            'hasRole',
            'lang',
        ]),
        displayedMembers() {
            return this.members.map(member => ({
                name: `${member.firstName} ${member.lastName} (${member.email})`,
                id: member.id,
            }));
        },
        locale() {
            return _datePickerLocales[this.lang];
        },
        priorityStatus() {
            return [
                {
                    name: this.$t('forms.labels.low'),
                    key: 'low',
                    color: '#0E9E62',
                },
                {
                    name: this.$t('forms.labels.normal'),
                    key: 'normal',
                    color: '#26384B',
                },
                {
                    name: this.$t('forms.labels.high'),
                    key: 'high',
                    color: '#DC582A',
                },
            ];
        },
        getActivePriorityStatusColor() {
            const priorityItem = this.priorityStatus[this.tempPriority] || {};
            if (priorityItem) {
                return priorityItem.color;
            }
            return '#26384B';
        },
        completionStatus() {
            return Object.values(workOrderStatusChoices).map(status => ({
                name: this.$t(`forms.labels.${status}`),
                isDisabled: status === 'completed',
                key: status,
            }));
        },
        isDisabled() {
            return !this.hasPermission('workOrders.edit') || !this.workOrder?.site?.client || this.isCompletedOrLocked;
        },
        dataCy() {
            return {
                workOrders,
            };
        },
    },
    watch: {
        async $route() {
            this.setData();
        },
        workOrder: {
            handler(val, old) {
                this.setData();
                if (val.completionStatus !== old.completionStatus) {
                    this.data.completionStatus = val.completionStatus;
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.setData();
    },
    methods: {
        ...mapActions({
            reloadWorkOrder: 'getWorkOrderInfo',
        }),
        getAssignedTo(user) {
            if (!user?.length) {
                return null;
            }
            const [assignedTo] = user;

            return assignedTo.ref;
        },
        formatDate(date) {
            return date ? this.$options.filters.formatDate(date, 'L') : null;
        },
        async getMembers() {
            const client = this.workOrder?.site?.client?.ref;
            // const queryParams = this.hasRole(roles.ADMINISTRATOR.value) ? {} : roles.ADMINISTRATOR.value;
            const allowedRoles = [
                roles.MANAGER.value,
                roles.SALES_REPRESENTATIVE.value,
                roles.SALES_MANAGER.value,
                roles.SERVICE_TECHNICIAN.value,
                roles.ROLE_OPERATOR.value];
            if (this.hasRole(roles.ADMINISTRATOR.value)) allowedRoles.push(roles.ADMINISTRATOR.value);
            const queryParams = {
                pagination: false,
                roles: allowedRoles,
            };
            let response;
            if (client) {
                response = await this.$rq.getClientMembers(client, queryParams);
                if (response && response.data) {
                    const { data } = response;
                    this.members = data.members;
                }
            }
        },
        convertDatesToDateTime(data, key) {
            // before posting to keep user timezone
            if (key === 'onLocationFrom' || key === 'onLocationTo') {
                return moment(data);
            }
            return data;
        },
        setData() {
            if (this.$route.params.isEdition) {
                this.isEdition = true;
            }
            this.tempPriority = this.priorityStatus.findIndex(item => item.key === this.workOrder.priority);
            this.getMembers();
            this.data.onLocationFrom = this.workOrder.onLocationFrom && typeof this.workOrder.onLocationFrom === 'object' ? moment(this.workOrder.onLocationTo).toString() : this.workOrder.onLocationFrom;
            this.data.onLocationTo = this.workOrder.onLocationTo && typeof this.workOrder.onLocationTo !== 'string' ? moment(this.workOrder.onLocationTo).toString() : this.workOrder.onLocationTo;
            this.data.assignedTo = this.getAssignedTo(this.workOrder.assignedTo);
            this.data.completionStatus = this.workOrder.completionStatus;
        },
        async onInput(key, newValue = null) {
            if (this.workOrder) {
                const value = this.convertDatesToDateTime(this.data[key], key);
                const payload = { [key]: newValue || value };
                const { id } = this.workOrder;
                // Update data
                if (id && payload) {
                    await this.$rq.updateWorkOrder(id, payload);
                    await this.reloadWorkOrder(id);
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
  @import "@/assets/styles/utils/_variables.scss";

  .wo-number-tag {
    display: inline-block;
    background-color: $primary-color;
    color: #fff;
    padding: 3px 8px;
  }
  .subtitle-2 {
    margin-bottom: 0.8rem;
    color: $primary-color;
  }
  ::v-deep .wo-number{
  background-color: #26384B;
  color: white;
  width: fit-content;
  font-size: 20px;
  font-weight: bold;
}
.flex--col {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}
::v-deep .v-slider--horizontal .v-slider__track-container {
    height: 12px;
}
::v-deep .v-slider__track-background {
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
}
::v-deep .v-slider__track-fill {
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
}
::v-deep .v-slider__thumb {
    background: white !important;
    width: 25px !important;
    height: 25px !important;
    border-width: 7px !important;
    border-style: solid !important;
    left: calc(25px / 2 * -1)
}
</style>
